import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  TableSortLabel,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import { Star, StarBorder, Search, Close } from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Symbol display name mapping
const SYMBOL_NAMES = {
  'BTC-USD': 'Bitcoin',
  'ETH-USD': 'Ethereum',
  'SOL-USD': 'Solana',
  'XRP-USD': 'Ripple',
  'DOGE-USD': 'Dogecoin',
  'NVDA': 'NVIDIA',
  'AAPL': 'Apple',
  'MSFT': 'Microsoft',
  'GOOGL': 'Alphabet',
  'AMZN': 'Amazon',
  'META': 'Meta',
  'TSLA': 'Tesla',
  'RIOT': 'Riot Platforms',
  'MARA': 'Marathon Digital',
  'COIN': 'Coinbase',
  'MSTR': 'MicroStrategy',
  'CLSK': 'CleanSpark',
  'HUT': 'Hut 8 Mining',
  'GLD': 'SPDR Gold',
  'SLV': 'iShares Silver'
};

// Get event color based on event type
const getEventChipColor = (eventType) => {
  if (!eventType) {return 'default';}
  
  const bullishEvents = [
    'higher_high', 'higher_low', 'swing_high_break',
    'cycle.low.higher_low', 'cycle.high.higher_high', 'cycle.low.sweep',
    'ema_bullish_alignment', 'ema_bullish_cloud_hit',
    'ema.cross.above', 'sma.cross.above', 'sma.reclaimed', 'dss.cross.bullish'
  ];
  
  const bearishEvents = [
    'lower_high', 'lower_low', 'swing_low_break', 'swing_high_sweep',
    'cycle.low.lower_low', 'cycle.high.lower_high', 'cycle.high.sweep',
    'ema_bearish_alignment', 'ema_bearish_cloud_hit',
    'ema.cross.below', 'sma.cross.below', 'sma.lost', 'dss.cross.bearish'
  ];
  
  if (bullishEvents.some(e => eventType.includes(e))) {return 'success';}
  if (bearishEvents.some(e => eventType.includes(e))) {return 'error';}
  if (eventType.includes('trendline')) {return 'warning';}
  
  return 'default';
};

const formatPrice = (price) => {
  if (price == null) {return '—';}
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (pct) => {
  if (pct == null || !Number.isFinite(pct)) {return '—';}
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

const MarketScanner = ({
  symbols,
  priceData = {},
  recentEvents = [],
  watchlist = [],
  onWatchlistToggle,
  loading = false,
  defaultWatchlistOnly = false,
  hideWatchlistFilter = false,
  // When `removeOnly`, the watch column shows a × icon that always
  // removes the row's symbol from the watchlist (instead of the
  // toggleable star). Used by ScannerWidget where every row is in
  // the watchlist by definition, so the star carries no information.
  removeOnly = false,
}) => {
  const [orderBy, setOrderBy] = useState('symbol');
  const [order, setOrder] = useState('asc');
  const [filter, setFilter] = useState('');
  const [showWatchlistOnly, setShowWatchlistOnly] = useState(defaultWatchlistOnly);

  // Get events for each symbol (last 48 hours)
  const symbolEvents = useMemo(() => {
    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    
    const eventsMap = {};
    recentEvents.forEach(event => {
      if (!event?.symbol) {return;}
      const eventDate = new Date(event.date);
      if (eventDate >= twoDaysAgo) {
        const normalizedSymbol = event.symbol.replace('/', '-');
        if (!eventsMap[normalizedSymbol]) {
          eventsMap[normalizedSymbol] = [];
        }
        eventsMap[normalizedSymbol].push(event);
      }
    });
    return eventsMap;
  }, [recentEvents]);

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let data = symbols.map(symbol => {
      const priceInfo = priceData[symbol] || {};
      const events = symbolEvents[symbol] || [];
      const isWatchlisted = watchlist.some(w => w.replace('/', '-') === symbol);
      
      return {
        symbol,
        name: SYMBOL_NAMES[symbol] || symbol,
        price: priceInfo.price,
        dayPct: priceInfo.dayPct,
        weekPct: priceInfo.weekPct,
        events,
        isWatchlisted
      };
    });

    // Apply filter
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      data = data.filter(d => 
        d.symbol.toLowerCase().includes(lowerFilter) ||
        d.name.toLowerCase().includes(lowerFilter)
      );
    }

    // Filter watchlist only
    if (showWatchlistOnly) {
      data = data.filter(d => d.isWatchlisted);
    }

    // Sort
    data.sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
      case 'symbol':
        aVal = a.symbol;
        bVal = b.symbol;
        break;
      case 'name':
        aVal = a.name;
        bVal = b.name;
        break;
      case 'price':
        aVal = a.price ?? -Infinity;
        bVal = b.price ?? -Infinity;
        break;
      case 'dayPct':
        aVal = a.dayPct ?? -Infinity;
        bVal = b.dayPct ?? -Infinity;
        break;
      case 'weekPct':
        aVal = a.weekPct ?? -Infinity;
        bVal = b.weekPct ?? -Infinity;
        break;
      default:
        return 0;
      }

      if (order === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return data;
  }, [symbols, priceData, symbolEvents, watchlist, filter, showWatchlistOnly, orderBy, order]);

  const columns = [
    { id: 'symbol', label: 'Symbol', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'price', label: 'Price', sortable: true },
    { id: 'dayPct', label: 'Day %', sortable: true },
    { id: 'weekPct', label: 'Week %', sortable: true },
    { id: 'events', label: 'Events', sortable: false },
    { id: 'watchlist', label: removeOnly ? '' : 'Watch', sortable: false }
  ];

  if (loading) {
    return (
      <Paper
        sx={{
          p: 2,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 200 }} />
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // Fill the parent (Panel content area). Without this the table caps
        // at the natural row height and leaves dead space when the parent
        // grid item is taller (e.g. matched against the Briefing column).
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Header with filters */}
      <Box sx={{ p: 2, pb: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          <TextField
            size="small"
            placeholder="Search symbols..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
          {!hideWatchlistFilter && (
            <Chip
              label={showWatchlistOnly ? 'Show All' : 'Watchlist Only'}
              onClick={() => setShowWatchlistOnly(!showWatchlistOnly)}
              color={showWatchlistOnly ? 'primary' : 'default'}
              variant={showWatchlistOnly ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer', alignSelf: 'flex-start' }}
            />
          )}
        </Box>
      </Box>

      {/* Table — fills the remaining space inside the Panel */}
      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: 'background.paper',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  {filter || showWatchlistOnly ? 'No matching symbols' : 'No data available'}
                </TableCell>
              </TableRow>
            ) : (
              processedData.map((row) => (
                <TableRow
                  key={row.symbol}
                  component={Link}
                  to={`/markets/${row.symbol}`}
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '&:last-child td': { borderBottom: 0 }
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>
                    {row.symbol}
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {formatPrice(row.price)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      color: row.dayPct != null 
                        ? (row.dayPct >= 0 ? 'success.main' : 'error.main')
                        : 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {formatPercent(row.dayPct)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      color: row.weekPct != null 
                        ? (row.weekPct >= 0 ? 'success.main' : 'error.main')
                        : 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {formatPercent(row.weekPct)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.events.slice(0, 3).map((event, idx) => (
                        <Chip
                          key={idx}
                          label={event.eventType?.replace(/_/g, ' ').substring(0, 12) || 'Event'}
                          size="small"
                          color={getEventChipColor(event.eventType)}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      ))}
                      {row.events.length > 3 && (
                        <Chip
                          label={`+${row.events.length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell onClick={(e) => e.preventDefault()} sx={{ width: 40 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!onWatchlistToggle) {return;}
                        if (removeOnly) {
                          // In removeOnly mode the action is always "take
                          // this off the watchlist" — the row exists
                          // *because* the symbol is on the watchlist.
                          onWatchlistToggle(row.symbol, false);
                        } else {
                          onWatchlistToggle(row.symbol, !row.isWatchlisted);
                        }
                      }}
                      aria-label={removeOnly ? `Remove ${row.symbol}` : 'Toggle watchlist'}
                      sx={removeOnly ? {
                        color: 'var(--dwlf-ink-30)',
                        '&:hover': { color: '#f85149' },
                      } : {
                        color: row.isWatchlisted ? 'warning.main' : 'text.disabled',
                        '&:hover': { color: 'warning.dark' },
                      }}
                    >
                      {removeOnly
                        ? <Close sx={{ fontSize: '1rem' }} />
                        : (row.isWatchlisted ? <Star /> : <StarBorder />)}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MarketScanner;
